// src/page/Privacy.jsx
import { Link } from "react-router-dom";

export default function Privacy() {
  const sections = [
    { id: "intro", label: "Introduction" },
    { id: "consumer-privacy", label: "Consumer Privacy (What We Collect)" },
    { id: "no-children", label: "No Information From Children" },
    { id: "tech", label: "Technology We Use (Cookies, Beacons, JS)" },
    { id: "third-party-cookies", label: "Third-Party Cookies & Analytics" },
    { id: "how-we-use", label: "How We Use & Share Information" },
    { id: "cross-device", label: "Cross-Device & Push Notifications" },
    { id: "tcpa", label: "TCPA Consent" },
    { id: "donottrack", label: "Third Parties & Do-Not-Track" },
    { id: "edit-request", label: "Editing / Requesting Your Information" },
    { id: "security", label: "Storage & Security" },
    { id: "minors", label: "Minors" },
    { id: "changes", label: "Changes to This Policy" },
    { id: "hipaa", label: "HIPAA Disclaimer" },
    { id: "california", label: "California Residents – Your Rights" },
    { id: "contact", label: "Contact Information" },
    { id: "updated", label: "Last Updated" },
  ];

  return (
    <main className="max-w-6xl mx-auto px-4 py-12">
      {/* breadcrumb */}
      <nav className="text-sm text-slate-500 mb-6">
        <Link to="/" className="hover:text-[#0A2540]">
          Home
        </Link>
        <span className="mx-2">›</span>
        <span className="text-slate-600">Legal</span>
        <span className="mx-2">›</span>
        <span className="text-[#0A2540] font-medium">Privacy Policy</span>
      </nav>

      {/* header */}
      <header className="mb-8">
        <p className="text-center text-xs font-semibold uppercase tracking-wide text-[#0A2540]/70">
          Legal
        </p>
        <h1 className="mt-2 text-center text-3xl md:text-4xl font-extrabold text-[#0A2540]">
          Privacy Policy
        </h1>
        <p className="mt-3 text-center text-slate-600">
          This policy explains what we collect, how we collect it, and how it’s
          used and shared.
        </p>
      </header>

      <div className="grid lg:grid-cols-[260px,1fr] gap-10">
        {/* TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <div className="rounded-xl border border-slate-200 bg-white">
              <div className="px-4 py-3 border-b border-slate-200 font-semibold text-[#0A2540]">
                On this page
              </div>
              <ul className="p-3 text-sm">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="block rounded px-3 py-2 text-slate-700 hover:bg-slate-50 hover:text-[#0A2540]"
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>

        {/* Article */}
        <article className="prose prose-slate max-w-none">
          <section id="intro" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Introduction</h2>
            <p>
              This Privacy Policy (together with our{" "}
              <Link to="/terms" className="underline text-[#0A2540]">
                Terms of Use
              </Link>
              ) explains what information we collect, how we collect it, how it
              is processed and shared, and your options. By submitting your
              details via this website, you consent to our collection and use of
              your personal information in accordance with this policy. This
              platform is intended for U.S. residents only.
            </p>
            <div className="mt-4 rounded-lg border-l-4 border-[#C89B3C] bg-[#FFF7E6] px-4 py-3">
              <strong>Attorney Advertising:</strong> MassTortPlaintiffs is a
              non-attorney marketing company. This is a paid advertisement for
              legal services. We are not a law firm and do not provide legal
              advice.
            </div>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="consumer-privacy" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">
              Consumer Privacy (What We Collect)
            </h2>
            <p>
              We generally collect the following kinds of information when you
              use this platform:
            </p>
            <ul>
              <li>
                <strong>Personal Information:</strong> name, address, phone,
                email, and other details you submit.
              </li>
              <li>
                <strong>Demographic Information:</strong> e.g., ZIP code,
                service interests, military status.
              </li>
              <li>
                <strong>Behavioral Information:</strong> pages visited, IP
                address, device data, timestamps.
              </li>
              <li>
                <strong>Third-Party Information:</strong> data provided by
                service providers and partners.
              </li>
              <li>
                <strong>Web Technology Information:</strong> items gathered by
                session replay tools (e.g., consent capture), form analytics,
                and security systems used to validate submissions and detect
                fraud.
              </li>
            </ul>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="no-children" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">
              No Information From Children
            </h2>
            <p>
              We do not knowingly collect personal information from children
              under 13. If you believe a child has provided information, please
              contact us and we will delete it promptly.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="tech" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">
              Technology We Use (Cookies, Beacons, JavaScript)
            </h2>
            <p>
              We and our partners use cookies, pixel tags, web beacons,
              JavaScript, and similar technologies to enable core functionality,
              measure engagement, provide analytics, and personalize content.
              You may control cookies in your browser; some features may not
              function without them.
            </p>
            <ul>
              <li>
                <strong>Cookies:</strong> store small amounts of data to
                remember preferences and sessions.
              </li>
              <li>
                <strong>Web Beacons:</strong> count views and email opens; used
                for analytics.
              </li>
              <li>
                <strong>JavaScript:</strong> enhances functionality and records
                user interactions to verify disclosures/consent.
              </li>
            </ul>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="third-party-cookies" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">
              Third-Party Cookies & Analytics
            </h2>
            <p>
              We work with third-party platforms (e.g., Google Analytics/Ads,
              session replay vendors) that place cookies or similar
              technologies. See Google’s opt-outs for Ads/Analytics and your
              browser/device controls for managing tracking. These third parties
              have their own privacy policies.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="how-we-use" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">
              How We Use & Share Information
            </h2>
            <p>We use your information to:</p>
            <ul>
              <li>
                Provide the services you requested and connect you with
                participating attorneys or partners.
              </li>
              <li>
                Validate submissions, verify consent, improve the site, and
                perform analytics.
              </li>
              <li>
                Market related services; deliver informational updates about
                investigations and settlements.
              </li>
              <li>
                Comply with law, enforce policies, and protect our rights and
                users.
              </li>
              <li>
                Support auditing, research, debugging, and security operations.
              </li>
            </ul>
            <p className="mt-3">
              We may share your information with participating attorneys,
              co-counsel, and service providers in order to respond to your
              inquiry and provide related services. We may also disclose data if
              required by law, to prevent fraud, or in connection with a merger
              or acquisition.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="cross-device" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">
              Cross-Device & Push Notifications
            </h2>
            <p>
              We may correlate usage across your devices to provide a consistent
              experience and measure campaign performance. We may send browser
              or device notifications; you can turn these off in your
              device/browser settings.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="tcpa" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">TCPA Consent</h2>
            <div className="rounded-lg border-l-4 border-[#C89B3C] bg-[#FFF7E6] px-4 py-3">
              By submitting your information, you agree we and participating
              attorneys may contact you via phone, text, and email (including
              using automated technology). Consent is not a condition of
              purchase. You may revoke consent at any time. Msg & data rates may
              apply. Text STOP to cancel, HELP for help.
            </div>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="donottrack" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">
              Third Parties & Do-Not-Track
            </h2>
            <p>
              Our site may contain links to third-party websites; their
              practices are governed by their privacy policies. We currently do
              not respond to browser “Do-Not-Track” signals. You can opt-out of
              many interest-based ads via platform-specific tools and industry
              opt-outs.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="edit-request" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">
              Editing / Requesting Your Information
            </h2>
            <p>
              To correct factual inaccuracies or request information we hold,
              contact us at{" "}
              <a
                className="underline text-[#0A2540]"
                href="mailto:info@masstortplaintiffs.com"
              >
                info@masstortplaintiffs.com
              </a>
              . We may retain data as required by law or for legitimate purposes
              (e.g., preventing abuse/fraud).
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="security" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Storage & Security</h2>
            <p>
              We use commercially reasonable safeguards (including HTTPS) to
              protect the information we collect. However, no method of
              transmission or storage is 100% secure. Keep your credentials
              confidential and contact us immediately if you suspect
              unauthorized access.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="minors" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Minors</h2>
            <p>
              This platform is intended for individuals 18+. If you are a
              parent/guardian and believe a minor has submitted information,
              please contact us so we can delete it.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="changes" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We’ll adjust
              the “Last Updated” date below and, where appropriate, provide
              additional notice. Continued use after changes indicates
              acceptance.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="hipaa" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">HIPAA Disclaimer</h2>
            <p>
              MassTortPlaintiffs is not a healthcare provider and is not a
              covered entity under HIPAA. Information submitted through this
              site is not protected by HIPAA and may be shared with
              participating attorneys for evaluation of your potential claim.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="california" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">
              California Residents – Your Rights
            </h2>
            <p>
              California residents have additional rights under the CCPA/CPRA
              (e.g., right to know, delete, correct, and opt-out of sale/share).
              See our{" "}
              <Link
                to="/california-privacy"
                className="underline text-[#0A2540]"
              >
                California Privacy Notice
              </Link>{" "}
              for details and how to exercise those rights.
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="contact" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Contact Information</h2>
            <p>
              Email:{" "}
              <a
                href="mailto:info@masstortplaintiffs.com"
                className="underline text-[#0A2540]"
              >
                info@masstortplaintiffs.com
              </a>
              <br />
              Mail: PO Box 214, Oceanside, NY 11572
            </p>
            <hr className="my-6 border-slate-200" />
          </section>

          <section id="updated" className="scroll-mt-28">
            <h2 className="!mb-2 text-[#0A2540]">Last Updated</h2>
            <p>April 30, 2025</p>
          </section>

          {/* Newsletter strip */}
          <section className="mt-12 rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-xl font-semibold text-[#0A2540]">
                  Sign Up For Our Newsletter
                </h3>
                <p className="text-slate-600">
                  Get new case updates, settlement deadlines, and breaking legal
                  news.
                </p>
              </div>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="flex w-full max-w-lg gap-2"
              >
                <input
                  type="text"
                  required
                  placeholder="First name *"
                  className="w-1/3 flex-1 rounded-lg border border-slate-300 px-3 py-2 outline-none focus:ring-2 focus:ring-[#C89B3C]"
                />
                <input
                  type="email"
                  required
                  placeholder="Email address *"
                  className="w-1/2 flex-1 rounded-lg border border-slate-300 px-3 py-2 outline-none focus:ring-2 focus:ring-[#C89B3C]"
                />
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-full bg-[#C89B3C] px-4 py-2 font-semibold text-white hover:bg-[#B8872D] transition"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Z" />
                    <path d="M12 3a7 7 0 0 0-7 7v4l-1.5 1.5a1 1 0 0 0 .7 1.7h15.6a1 1 0 0 0 .7-1.7L19 14v-4a7 7 0 0 0-7-7Z" />
                  </svg>
                  Subscribe
                </button>
              </form>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}
